import {
    TableRow,
    Hidden
} from "@mui/material";
import ListingCheckbox, {ListingCheckboxPlaceholder} from "components/Product/Table/ListingCheckbox";
import ListingArticleCell from "components/Product/Table/ListingArticleCell";
import ListingMobileArticleCell from "components/Product/Table/ListingMobileArticleCell";
import ListingStockCell from "components/Product/Table/ListingStockCell";
import ListingTableCell from "components/Product/Table/ListingTableCell";
import ListingProductImageCell from "components/Product/Table/ListingProductImageCell";
import ListingPriceCell from "components/Product/Table/ListingPriceCell";
import ListingSalesCell, {computeTotalSales} from "components/Product/Table/ListingSalesCell";
import ListingScoringCell from "components/Product/Table/ListingScoringCell";
import updateProductRanking from "api/product/updateRanking";
import {getContextualRank} from "utils/contextualRank";
import {hasProductEditionAuthorization} from "../../../utils/authorizations";
import {getFirstProductImage} from "../../../utils/getFirstProductImage";
import React from "react";
import {styled} from "@mui/material/styles";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import listStyle from "../../../assets/jss/views/product/listStyle";
import {injectIntl} from "react-intl";
import {sortOn} from "../../../utils/sortOn";
import {marketPlaceOffer} from "../../enum/offers";
import {handleSelectProduct} from "../../../actions/product/selection";
import {UserAgentUtils} from "../../../utils/useragent";
import {getTranslation} from "../../helpers/translations";
import {withStyles} from "@mui/styles";

// checkbox vertically centered next to the image column, as in the design
const ImageAndCheckbox = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

class ProductRow extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isSelected: false,
        }
    }

    renderArticleCell = (product) => (
        <ListingArticleCell
            name={getTranslation(product).name}
            isEikow={product.eikow}
            brandName={product.brand?.name}
            sku={product.sku}
            eshopId={product.eshopId}
            categories={product.categories
                .map((category) => ({id: category.id, name: getTranslation(category).name, draft: category.draft}))
                .sort(sortOn("name"))}
            updatedAt={product.updatedAt}
            manualUpdatedAt={product.manualUpdatedAt}
            manualUpdatedBy={product.manualUpdatedBy ?? product.manualUpdatedByName}
        />
    );

    // below the sm breakpoint the row keeps two columns and this cell stacks
    // what the hidden price / sales / stock columns carry
    renderMobileArticleCell = (product) => {
        const isMarketplace = this.props.offer === marketPlaceOffer;

        return (
            <ListingMobileArticleCell
                name={getTranslation(product).name}
                isEikow={product.eikow}
                brandName={product.brand?.name}
                sku={product.sku}
                active={isMarketplace ? product.status : undefined}
                validated={isMarketplace ? product.validated : undefined}
                hasPushError={isMarketplace ? product.hasPushError : undefined}
                validationErrors={product.validationErrors}
                ctr={product.ctr}
                price={product.lowestPrice}
                previousPrice={product.highestPrice}
                currency={this.props.currentOrganization?.currency}
                priceCompetitiveness={product.priceCompetitiveness}
                totalSales={computeTotalSales(product)}
                stocks={(product.variations ?? []).map((variation) => ({
                    id: variation.id,
                    label: variation.name,
                    stock: variation.stock,
                }))}
            />
        );
    };

    renderStockCell = (product) => {
        const isMarketplace = this.props.offer === marketPlaceOffer;

        return (
            <ListingStockCell
                active={isMarketplace ? product.status : undefined}
                validated={isMarketplace ? product.validated : undefined}
                hasPushError={isMarketplace ? product.hasPushError : undefined}
                validationErrors={product.validationErrors}
                siteUrl={product.status ? product.url : null}
                stocks={(product.variations ?? []).map((variation) => ({
                    id: variation.id,
                    label: variation.name,
                    stock: variation.stock,
                }))}
            />
        );
    };

    renderPriceCell = (product) => (
        <ListingPriceCell
            price={product.lowestPrice}
            // pre-discount price of the same (lowest-priced) variation,
            // null when it is not discounted
            previousPrice={product.highestPrice}
            currency={this.props.currentOrganization?.currency}
            priceCompetitiveness={product.priceCompetitiveness}
        />
    );

    handleSelect = (item, selected) => {
        this.props.handleSelectProduct(item, selected);
    }


    handleEditProduct = (event, product, currentPage, currentOrderBy) => {
        if (window.getSelection().toString().length > 0) {
            return;
        }

        if (!hasProductEditionAuthorization(this.props.authorizations)) {
            return;
        }

        const url = `${this.props.baseParamsUrl + this.props.match.params.id}/products/show/${product.id}`;
        if (event.metaKey || event.ctrlKey) {
            window.open(url, '_blank');
        } else {
            this.props.history.push({
                pathname: url,
                currentPage: currentPage,
                currentOrderBy: currentOrderBy,
                type: "parent",
            });
        }
    }

    render() {
        const product = this.props.product;
        const classes = this.props.classes;
        const currentPage = this.props.currentPage;
        const currentOrderBy = this.props.currentOrderBy;
        const isSelected = this.state.isSelected;
        const selected = this.props.selected;
        let hasHover = false;

        // selection (batch actions) and the inline ranking edit share the same rule
        const canEditProducts = hasProductEditionAuthorization(this.props.authorizations);

        if (canEditProducts) {
            hasHover = true;
        }

        return (
            <TableRow
                onClick={(e) => this.handleEditProduct(e, product, currentPage, currentOrderBy)}
                hover={hasHover}
                tabIndex={-1}
                key={product.id}
                aria-checked={isSelected}
                selected={isSelected}
                classes={{hover: classes.tableRowHover}}
            >
                <ListingTableCell className={UserAgentUtils.isMobile() ? classes.tableCell : ''}>
                    <ImageAndCheckbox>
                        {/* The header always shows a checkbox when the column has one, so a
                            row that cannot be selected keeps its slot rather than sliding
                            its content 24px left of the column label. */}
                        {!UserAgentUtils.isMobile() && canEditProducts && (
                            (false === product.eikow || this.props.offer === marketPlaceOffer)
                                ? <ListingCheckbox
                                    checked={this.props.isChecked}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleSelect(product, selected)
                                    }}
                                />
                                : <ListingCheckboxPlaceholder/>
                        )}
                        <ListingProductImageCell
                            showRanking
                            ranking={getContextualRank(product.rankings, product.retailerId)}
                            onRankingSave={canEditProducts ? (ranking) => updateProductRanking(product.id, ranking) : undefined}
                            src={product.pictures.length > 0 ? getFirstProductImage(product.pictures) : null}
                            alt="product_image"
                            groupedSearchImpression={product.groupedSearchImpression}
                        />
                    </ImageAndCheckbox>
                </ListingTableCell>
                <ListingTableCell className={UserAgentUtils.isMobile() ? classes.tableCell : ''}>
                    <Hidden smDown>
                        {this.renderArticleCell(product)}
                    </Hidden>
                    <Hidden smUp>
                        {this.renderMobileArticleCell(product)}
                    </Hidden>
                </ListingTableCell>
                <Hidden smDown>
                    <ListingTableCell>
                        {this.renderPriceCell(product)}
                    </ListingTableCell>
                </Hidden>
                <Hidden smDown>
                    <ListingTableCell>
                        <ListingSalesCell
                            onlineSales={product.onlineSales}
                            totalSales={computeTotalSales(product)}
                        />
                    </ListingTableCell>
                </Hidden>
                <Hidden smDown>
                    <ListingTableCell>
                        <ListingScoringCell
                            score={product.score}
                            scoring={product.scoring}
                            scoringDate={product.scoringDate}
                            completeness={product.validationScore}
                            ctr={product.ctr}
                        />
                    </ListingTableCell>
                </Hidden>
                <Hidden smDown>
                    <ListingTableCell>
                        {this.renderStockCell(product)}
                    </ListingTableCell>
                </Hidden>
            </TableRow>
        );
    }
}

ProductRow.propTypes = {
    classes: PropTypes.object.isRequired,
    selected: PropTypes.array,
};

const mapStateToProps = state => {
    return {
        authorizations: state.authentication.authorizations,
        retailer: state.retailer.show.retrieved,
        selected: state.product.selection.selected,
        isSelectAll: state.product.selection.isSelectAll,
        currentOrganization: state.currentOrganization.retrieved ?? null,
    };
};

const mapDispatchToProps = dispatch => ({
    handleSelectProduct: (product, selected) => dispatch(handleSelectProduct(product, selected)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(listStyle)(injectIntl(ProductRow)));
